import React from "react";
import { Link } from "react-router-dom";
import {
  TwitterLogo,
  FacebookLogo,
  LinkedinLogo,
  InstagramLogo,
} from "@phosphor-icons/react";
import { FarmwatchLogoDark } from "@/components/FarmwatchLogo";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    news: [
      { label: "All News", href: "/" },
      { label: "Ireland", href: "/?region=ireland" },
      { label: "UK", href: "/?region=uk" },
      { label: "USA", href: "/?region=usa" },
    ],
    markets: [
      { label: "Commodities", href: "/#markets" },
      { label: "Livestock", href: "/?category=livestock" },
      { label: "Agri Stocks", href: "/agri-stocks" },
      { label: "Dairy", href: "/?category=dairy" },
    ],
    company: [
      { label: "About Us", href: "/about" },
      { label: "Contact", href: "/contact" },
      { label: "System Status", href: "/status" },
      { label: "Advertise", href: "/advertise" },
      { label: "Careers", href: "/careers" },
    ],
    legal: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Cookie Policy", href: "/cookies" },
    ],
  };

  return (
    <footer className="bg-[#111827] text-[#E5E7EB]">
      {/* Main Footer */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <FarmwatchLogoDark size={36} variant="full" />
            </Link>
            <p className="text-sm text-[#8B8580] font-sans mb-4">
              Your trusted source for agricultural news, market prices, and farming insights across Ireland, UK, USA, and global markets.
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              {[
                { icon: TwitterLogo, href: "#", label: "Twitter" },
                { icon: FacebookLogo, href: "#", label: "Facebook" },
                { icon: LinkedinLogo, href: "#", label: "LinkedIn" },
                { icon: InstagramLogo, href: "#", label: "Instagram" },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="w-8 h-8 flex items-center justify-center border border-[#374151] text-[#8B8580] hover:border-[#0F766E] hover:text-[#0F766E] transition-colors"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* News Links */}
          <div>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-[#0F766E] mb-4">
              News
            </h4>
            <ul className="space-y-2">
              {footerLinks.news.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm font-sans text-[#8B8580] hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Markets Links */}
          <div>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-[#0F766E] mb-4">
              Markets
            </h4>
            <ul className="space-y-2">
              {footerLinks.markets.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm font-sans text-[#8B8580] hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-[#0F766E] mb-4">
              Company
            </h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm font-sans text-[#8B8580] hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-[#0F766E] mb-4">
              Legal
            </h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm font-sans text-[#8B8580] hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#374151]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[#8B8580] font-sans">
              © {currentYear} Farmwatch. All rights reserved.
            </p>
            <p className="text-xs text-[#8B8580] font-sans">
              Market data provided by Alpha Vantage. News aggregated from trusted sources.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
